import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Guid, IDeleteEntity, IGenericResponse } from '../shared';
import { LoggingService } from '../shared/services/logging/logging.service';
import { IChargeNumberManagement } from './IChargeNumberManagement';
import { IContractManagement } from './IContractManagement';
import { IFundingMod } from './IFundingMod';
import { IInvoiceCycle } from './IInvoiceCycle';
import { IContract } from './presenters/contract-management-presenters.component';

@Injectable({
    providedIn: 'root'
})
export class ContractManagementService {
    constructor(private http: HttpClient, private log: LoggingService) {}

    private contractUri = `${environment.apiLocation}/ContractMgmt`;
    private chargeNumberUri = `${environment.apiLocation}/CNMgmt`;
    private fundingModUri = `${environment.apiLocation}/FundingMod`;
    private invoiceCycleUri = `${environment.apiLocation}/InvoiceCycle`;

    public getContracts(
        Activeonly?: boolean
    ): Observable<Array<IContractManagement>> {
        this.log.verbose({
            type: 'ContractManagementService:getContracts',
            uri: this.contractUri
        });
        return this.http
            .post<Array<IContractManagement>>(this.contractUri, { Activeonly })
            .pipe(
                tap(res =>
                    this.log.verbose({
                        type: 'ContractManagementService:reponse',
                        res
                    })
                )
            );
    }

    public getChargeNumbers(Contract: {
        ContractListID: string;
        Activeonly: boolean;
    }): Observable<Array<IChargeNumberManagement>> {
        return this.http
            .post<Array<IChargeNumberManagement>>(
                this.chargeNumberUri,
                Contract
            )
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'ContractManagementService:getChargeNumbers',
                        data
                    })
                ),
                map(data =>
                    data.map(x => ({
                        ...x,
                        id: x.CNListID ? x.CNListID : Guid.newGuid(),
                        BurnRateStartDate: x.BurnRateStartDate
                            ? moment(x.BurnRateStartDate).toDate()
                            : null,
                        CNStartDate: x.CNStartDate
                            ? moment(x.CNStartDate).toDate()
                            : null,
                        CNEndDate: x.CNEndDate
                            ? moment(x.CNEndDate).toDate()
                            : null
                    }))
                )
            );
    }

    public getFundingMod(contract: IContract): Observable<Array<IFundingMod>> {
        return this.http
            .post<Array<IFundingMod>>(this.fundingModUri, contract)
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'ContractManagementService:getFundingMod',
                        data
                    })
                ),
                map(data =>
                    data.map(x => ({
                        ...x,
                        id: x.FundingID ? x.FundingID : Guid.newGuid()
                    }))
                )
            );
    }

    public getInvoiceCycle(): Observable<Array<IInvoiceCycle>> {
        this.log.verbose({
            type: 'ContractManagementService:getInvoiceCycle',
            uri: this.invoiceCycleUri
        });
        return this.http.get<Array<IInvoiceCycle>>(this.invoiceCycleUri).pipe(
            tap(res =>
                this.log.verbose({
                    type: 'ContractManagementService:reponse',
                    res
                })
            ),
            map(data =>
                data.map(x => ({
                    ...x,
                    id: `${Guid.newGuid()}`
                }))
            )
        );
    }

    public putContractMgmt(
        contractMgmtData: Array<IContractManagement>
    ): Observable<IGenericResponse<IContractManagement>> {
        return this.http
            .put<IGenericResponse<IContractManagement>>(this.contractUri, {
                ContractMgmtList: contractMgmtData
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'ContractManagementService:putContractMgmt',
                        data
                    })
                )
            );
    }

    public putCNMgmt(
        cNMgmtData: Array<IChargeNumberManagement>
    ): Observable<IGenericResponse<IChargeNumberManagement>> {
        return this.http
            .put<IGenericResponse<IChargeNumberManagement>>(
                this.chargeNumberUri,
                { CNMgmtList: cNMgmtData }
            )
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'ContractManagementService:putCNMgmt',
                        data
                    })
                )
            );
    }

    public putFundingMod(
        fundingModData: Array<IFundingMod>
    ): Observable<IGenericResponse<IFundingMod>> {
        return this.http
            .put<IGenericResponse<IFundingMod>>(this.fundingModUri, {
                FundingModList: fundingModData
            })
            .pipe(
                tap(data =>
                    this.log.verbose({
                        type: 'ContractManagementService:putFundingMod',
                        data
                    })
                )
            );
    }

    public deleteFundingMod(ids: Array<string>) {
        return this.http
            .request<IGenericResponse<IDeleteEntity>>(
                'DELETE',
                this.fundingModUri,
                {
                    body: { EntityIDs: ids }
                }
            )
            .pipe(
                tap(data => {
                    this.log.verbose({
                        type: 'ContractManagementService:deleteFundingMod:reponse',
                        data
                    });
                })
            );
    }
}
